import { useState } from 'react';
import { Menu, X, Linkedin } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

// External links
const IRONCLAD_URL = 'https://ironclad.williamsdiversified.com/';
const LINKEDIN_URL = 'https://www.linkedin.com/company/williams-diversified/';

function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  // Navigation links - Ironclad is now a regular nav item (no separate Portal)
  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Industries', path: '/industries' },
    { name: 'Government', path: '/government-contracting' },
    { name: 'Certifications', path: '/certifications' },
    { name: 'Careers', path: '/careers' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav className="fixed w-full top-0 z-50 bg-black/95 backdrop-blur-md shadow-lg">
      <div className="absolute inset-0 brass-lighting"></div>
      <div className="absolute inset-0 geometric-brass"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header height ~112px with py-3 padding */}
        <div className="flex justify-between items-center h-28 py-3">
          
          {/* Logo - ~96px tall */}
          <Link to="/" className="flex-shrink-0">
            <picture>
              <source srcSet="/images/wd_logo_navbar.webp" type="image/webp" />
              <img 
                src="/images/wd_logo_navbar.png" 
                alt="Williams Diversified Logo" 
                className="h-24 w-auto"
                width="138"
                height="96"
                loading="eager"
                fetchPriority="high"
                data-testid="company-logo"
              />
            </picture>
          </Link>
          
          {/* Desktop Navigation - breakpoint at xl (1280px) for more room */}
          <div className="hidden xl:flex items-center gap-7">
            {navLinks.map((link) => (
              <Link 
                key={link.path}
                to={link.path}
                className={`text-white hover:text-gold transition-colors font-medium text-base whitespace-nowrap ${
                  isActive(link.path) ? 'text-gold' : ''
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Ironclad - same style as other nav links */}
            <a 
              href={IRONCLAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-gold transition-colors font-medium text-base whitespace-nowrap"
              data-testid="nav-ironclad"
            >
              Ironclad
            </a>
            
            {/* LinkedIn Icon */}
            <a 
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-gold transition-colors ml-1"
              aria-label="Williams Diversified LinkedIn"
              data-testid="nav-linkedin"
            >
              <Linkedin size={20} />
            </a>
          </div>

          {/* Mobile menu button - show below xl breakpoint */}
          <button 
            className="xl:hidden text-gold p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div 
          className="xl:hidden bg-black/98 border-t border-gold/30 max-h-[80vh] overflow-y-auto relative z-50" 
          data-testid="mobile-menu"
        >
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block w-full text-left px-4 py-3 text-white hover:text-gold hover:bg-gold/10 transition-colors rounded ${
                  isActive(link.path) ? 'text-gold bg-gold/10' : ''
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Ironclad - same style as other mobile nav links */}
            <a 
              href={IRONCLAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-left px-4 py-3 text-white hover:text-gold hover:bg-gold/10 transition-colors rounded"
              data-testid="mobile-nav-ironclad"
            >
              Ironclad
            </a>
            
            {/* Mobile LinkedIn */}
            <a 
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 w-full text-left px-4 py-3 text-white hover:text-gold hover:bg-gold/10 transition-colors rounded"
            >
              <Linkedin size={18} />
              <span>LinkedIn</span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
